// Infinite scroll loader component
// CREATED: Detects when user scrolls to bottom and triggers loading more data
// Features:
// - Uses Intersection Observer API for efficient scroll detection
// - Shows loading spinner while fetching
// - Configurable threshold for early triggering
// - Touch-target optimized (44px min height)

import { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';

interface InfiniteScrollLoaderProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  threshold?: number;
}

export function InfiniteScrollLoader({
  onLoadMore,
  hasMore,
  isLoading,
  threshold = 0.8,
}: InfiniteScrollLoaderProps) {
  const observerTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!hasMore || isLoading) return;

    const target = observerTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      {
        threshold,
        rootMargin: '100px',
      }
    );

    observer.observe(target);

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, isLoading, onLoadMore, threshold]);

  if (!hasMore) {
    return (
      <div className="py-6 text-center">
        <p className="text-sm text-gray-500">Tous les éléments ont été chargés</p>
      </div>
    );
  }

  return (
    <div
      ref={observerTarget}
      className="flex items-center justify-center py-6"
      style={{ minHeight: '44px' }}
    >
      {isLoading && (
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="animate-spin" size={20} />
          <span className="text-sm">Chargement...</span>
        </div>
      )}
    </div>
  );
}
